import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { can, useOrg } from "../org";
import { CARE_STATE, Initials, Rhythm, Select, SourceTag, displayName } from "./bits";
import { PersonForm } from "./PersonForm";
import { RequestDialog, RequestsPanel } from "./Requests";
import { lastText } from "../care/text";
import { BulkBar } from "./Bulk";

const why = (p) => (p.groups.length ? p.groups.map((g) => g.name).join(" · ") : p.household?.name || null);

const Row = ({ p, seesCare, sel }) => {
  const navigate = useNavigate();
  const on = sel?.ids.has(p.id);
  return (
    <button onClick={() => (sel ? sel.toggle(p.id) : navigate(`/people/${p.id}`))} aria-pressed={sel ? on : undefined} className="w-full text-left py-5 flex gap-4 items-start group" data-testid={`person-row-${p.id}`}>
      {sel && <input type="checkbox" readOnly checked={on} className="mt-4 w-5 h-5 accent-[var(--moss)] shrink-0" data-testid={`person-select-${p.id}`} />}
      <Initials p={p} size={52} />
      <div className="flex-1 min-w-0">
        <p className="t-name group-hover:text-brand transition-colors">{displayName(p)}</p>
        {why(p) && <p className="t-body mt-1 text-ink-soft truncate" data-testid={`person-row-why-${p.id}`}>{why(p)}</p>}
        {seesCare && <p className="t-meta mt-0.5" data-testid={`person-row-last-${p.id}`}>{lastText(p.last_connection)}</p>}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2.5">
          {seesCare && <Rhythm r={p.rhythm} compact testid={`person-row-rhythm-${p.id}`} />}
          {p.caregiver ? <span className="owner text-sm" data-testid={`person-row-caregiver-${p.id}`}>Cared for by <b>{p.caregiver.name}</b></span>
            : <span className="status status-attention" data-testid={`person-row-caregiver-${p.id}`}>No Assigned Care Leader yet</span>}
          <SourceTag x={p} testid={`person-row-source-${p.id}`} />
        </div>
      </div>
    </button>
  );
};

const StateGroup = ({ state, people, seesCare, sel }) => {
  if (!people.length) return null;
  const s = CARE_STATE[state];
  return (
    <section className="mt-10 first:mt-8" data-testid={`people-group-${state || "all"}`}>
      {s && <div className="flex items-baseline gap-2"><h2 className="t-heading">{s.label}</h2><span className="text-[13px] text-ink-faint tabular-nums" data-testid={`people-group-count-${state}`}>{people.length}</span></div>}
      <div className="mt-3 rule-list border-t border-line">{people.map((p) => <Row key={p.id} p={p} seesCare={seesCare} sel={sel} />)}</div>
    </section>
  );
};

const Filters = ({ f, setF, groups, c }) => (
  <div className="inline-flex flex-wrap items-center gap-0.5 mt-4 p-1 rounded-[14px] bg-sunk max-w-full" data-testid="people-filters">
    {c.seesCare && [["", "Everyone"], ["mine", "Assigned to me"], ["unassigned", "No Assigned Care Leader"]].map(([k, l]) => (
      <button key={k} aria-pressed={f.view === k} onClick={() => setF({ ...f, view: k })} className="filter !h-10 !rounded-[10px] aria-pressed:shadow-soft" data-testid={`people-filter-${k || "all"}`}>{l}</button>
    ))}
    {c.manage && <button aria-pressed={f.view === "archived"} onClick={() => setF({ ...f, view: f.view === "archived" ? "" : "archived" })} className="filter !h-10 !rounded-[10px] aria-pressed:shadow-soft" data-testid="people-filter-archived">Archived</button>}
    {c.seesCare && groups.length > 0 && <span className="hidden sm:block w-px h-5 bg-line mx-1" aria-hidden="true" />}
    {groups.length > 0 && (
      <Select value={f.group} onChange={(v) => setF({ ...f, group: v })} testid="people-filter-group" className={`!h-10 !w-auto !text-[16px] !font-medium !rounded-[10px] !border-transparent !shadow-none ${f.group ? "!bg-surface text-ink" : "!bg-transparent text-ink-soft"}`}>
        <option value="">All groups</option>
        {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
      </Select>
    )}
  </div>
);

const Aside = ({ people, c, onAsk, reqKey, load }) => {
  const unassigned = (people || []).filter((p) => !p.caregiver).length;
  return (
    <aside className="lg:sticky lg:top-12 space-y-8" data-testid="people-aside">
      {people && (
        <div className="grid grid-cols-2 gap-6 border-y border-line py-6">
          <div><p className="t-metric" data-testid="people-count">{people.length}</p><p className="t-meta mt-1">{people.length === 1 ? "person" : "people"}</p></div>
          <div><p className={`t-metric ${unassigned ? "text-sun" : ""}`} data-testid="people-unassigned-count">{unassigned}</p><p className="t-meta mt-1">without an Assigned Care Leader</p></div>
        </div>
      )}
      <RequestsPanel key={reqKey} people={people || []} onChange={load} />
      {c.leader && (
        <div><p className="t-body text-ink-soft">Can't find someone?</p>
          <button onClick={onAsk} className="btn btn-quiet mt-3 w-full" data-testid="people-request-btn">Ask to have them added</button></div>
      )}
    </aside>
  );
};

export default function People() {
  const { api, membership, onTenantError } = useOrg();
  const c = can(membership);
  const [people, setPeople] = useState(null);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ view: "", group: null });
  const [adding, setAdding] = useState(false);
  const [asking, setAsking] = useState(false);
  const [reqKey, setReqKey] = useState(0);
  const [selecting, setSelecting] = useState(false);
  const [ids, setIds] = useState(new Set());
  const archivedView = f.view === "archived";
  const sel = selecting ? { ids, toggle: (id) => setIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }) } : null;
  const stopSelecting = () => { setSelecting(false); setIds(new Set()); };
  const load = () => {
    const qs = new URLSearchParams({ q, ...(f.group ? { group_id: f.group } : {}), ...(f.view === "mine" ? { mine: true } : {}), ...(f.view === "unassigned" ? { unassigned: true } : {}), ...(archivedView ? { archived: true } : {}) });
    return api(`/people?${qs}`).then(setPeople).catch((e) => { onTenantError(e); toast.error(e.message); });
  };
  useEffect(() => { const h = setTimeout(load, 200); return () => clearTimeout(h); }, [api, q, f]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api("/groups").then(setGroups).catch(() => {}); }, [api]);
  const states = c.seesCare && !archivedView ? ["active", "regular", "not_monitored"] : [null];
  return (
    <div data-testid="people-screen">
      <div className="flex items-end justify-between gap-4">
        <div><p className="t-label">{c.leader ? "People in your care" : membership.org_name}</p><h1 className="t-greeting mt-3">{c.leader ? "My People" : "People"}</h1></div>
        <div className="flex gap-2 shrink-0">
          {c.directory && <button onClick={() => (selecting ? stopSelecting() : setSelecting(true))} className="btn btn-outline btn-sm" data-testid="people-select-btn">{selecting ? "Cancel" : "Select"}</button>}
          {selecting && people?.length > 0 && <button onClick={() => setIds(new Set(people.map((p) => p.id)))} className="btn btn-quiet btn-sm" data-testid="people-select-all">Select all {people.length}</button>}
          {c.directory && !selecting && <button onClick={() => setAdding(true)} className="btn btn-primary btn-sm" data-testid="people-add-btn"><Plus size={18} /> Add person</button>}
        </div>
      </div>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-x-14 gap-y-8 mt-8">
        <div className="min-w-0">
          <label className="field h-14 px-4 flex items-center gap-3">
            <Search size={20} className="text-ink-faint" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or household" className="flex-1 bg-transparent outline-none placeholder:text-ink-faint" data-testid="people-search-input" />
          </label>
          <Filters f={f} setF={setF} groups={groups} c={c} />
          <div data-testid="people-list">
            {people && states.map((s) => <StateGroup key={s || "all"} state={s} seesCare={c.seesCare} sel={sel} people={s ? people.filter((p) => p.care_state === s) : people} />)}
          </div>
          {people?.length === 0 && (
            <div className="mt-10" data-testid="people-empty">
              {archivedView ? <p className="text-ink-soft">No one is archived.</p> : q || f.view || f.group ? <p className="text-ink-soft">No one matches. Try a different name or filter.</p>
                : c.leader ? <><p className="t-name-sm">No one is in your care yet.</p><p className="t-body text-ink-soft mt-1">When a pastor assigns someone to you, or adds you to lead a Care Group, they'll appear here.</p></>
                : <><p className="t-name-sm">No people yet.</p><p className="t-body text-ink-soft mt-1">Add the first person to start caring for your church here.</p></>}
            </div>
          )}
          {!people && <p className="mt-10 t-meta">Loading…</p>}
        </div>
        <Aside people={people} c={c} onAsk={() => setAsking(true)} reqKey={reqKey} load={load} />
      </div>
      {selecting && ids.size > 0 && <BulkBar ids={[...ids]} c={c} archivedView={archivedView} onClear={stopSelecting} onDone={() => { setIds(new Set()); load(); }} />}
      {adding && <PersonForm onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {asking && <RequestDialog onClose={(sent) => { setAsking(false); if (sent) setReqKey((k) => k + 1); }} />}
    </div>
  );
}
